import { useKiosk } from "application/contexts/kiosk/KioskContext";
import {
  ResolutionType,
  useScreenStore,
} from "application/contexts/screen/ScreenStore";
import BottomModal from "components/bottomModal/BottomModal";
import React, { useCallback, useMemo, useState } from "react";
import { useAdmin } from "../../../application/contexts/admin/adminContext";
import { OrderLog, PrintStatus } from "../../../domains/order/agreggates/order/Order";
import "./OrderLogView.css";
import styles from "./OrderLogViewV3.module.scss";
import { OrderList } from "./orderList/OrderList";
import { OrderLogItemV3 } from "./orderLogItem/OrderLogItemV3";

const PAGE_SIZE = 50;

type PaymentFilter = "all" | "approved" | "pending";
type SyncFilter = "all" | "synced" | "unsynced";
type PrintFilter = "all" | "success" | "pending" | "error";

interface Filters {
  search: string;
  payment: PaymentFilter;
  sync: SyncFilter;
  print: PrintFilter;
}

const INITIAL_FILTERS: Filters = {
  search: "",
  payment: "all",
  sync: "all",
  print: "all",
};

const matchesSearch = (order: OrderLog, term: string): boolean => {
  if (!term) return true;
  const t = term.toLowerCase().trim();
  const friendlyId = order.friendlyId?.toLowerCase() ?? "";
  const document = order.customer?.document?.toLowerCase() ?? "";
  const cardNumber = order.payment?.cardNumber?.toLowerCase() ?? "";
  return (
    friendlyId.includes(t) ||
    document.includes(t) ||
    cardNumber.includes(t)
  );
};

const matchesPayment = (order: OrderLog, filter: PaymentFilter): boolean => {
  if (filter === "all") return true;
  if (filter === "approved") return !!order.paymentApproved;
  return !order.paymentApproved;
};

const matchesSync = (order: OrderLog, filter: SyncFilter): boolean => {
  if (filter === "all") return true;
  if (filter === "synced") return !!order.isSynchronized;
  return !order.isSynchronized;
};

const matchesPrint = (order: OrderLog, filter: PrintFilter): boolean => {
  if (filter === "all") return true;
  if (filter === "success") return order.printStatus === PrintStatus.SUCCESS;
  if (filter === "error") return order.printStatus === PrintStatus.ERROR;
  return order.printStatus === PrintStatus.PENDING || order.printStatus == null;
};

export const OrderLogViewV3 = () => {
  const { orderLog, onOrderLogReprinted } = useAdmin();
  const { kiosk } = useKiosk();
  const { resolution } = useScreenStore();

  const [modalPreviewOpen, setModalPreviewOpen] = useState(false);
  const [itemSelectedView, setItemSelectedView] = useState<OrderLog | null>(null);
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);

  const onCloseModal = useCallback(() => {
    setModalPreviewOpen(false);
    setItemSelectedView(null);
    onOrderLogReprinted();
  }, [onOrderLogReprinted]);

  const onClickPreview = useCallback((orderItem: OrderLog) => {
    setModalPreviewOpen(true);
    setItemSelectedView(orderItem);
  }, []);

  const setFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setPage(0);
    },
    []
  );

  const onClearFilters = useCallback(() => {
    setFilters(INITIAL_FILTERS);
    setPage(0);
  }, []);

  const orderLogSorted = useMemo(() => {
    return [...orderLog].sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [orderLog]);

  const filtered = useMemo(() => {
    return orderLogSorted.filter(
      (o) =>
        matchesSearch(o, filters.search) &&
        matchesPayment(o, filters.payment) &&
        matchesSync(o, filters.sync) &&
        matchesPrint(o, filters.print)
    );
  }, [orderLogSorted, filters]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  const pageItems = useMemo(() => {
    const start = page * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const hasActiveFilters =
    filters.search !== "" ||
    filters.payment !== "all" ||
    filters.sync !== "all" ||
    filters.print !== "all";

  return (
    <div id={styles.OrderLogViewV3}>
      <div className={styles.filterBar}>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="Buscar por ID, CPF ou cartão..."
          value={filters.search}
          onChange={(e) => setFilter("search", e.target.value)}
        />

        <div className={styles.filterRow}>
          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Pagamento</span>
            <div className={styles.toggleGroup}>
              {(["all", "approved", "pending"] as PaymentFilter[]).map((v) => (
                <button
                  key={v}
                  className={`${styles.toggleBtn} ${filters.payment === v ? styles.active : ""}`}
                  onClick={() => setFilter("payment", v)}
                >
                  {v === "all" ? "Todos" : v === "approved" ? "Aprovado" : "Pendente"}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Sincronização</span>
            <div className={styles.toggleGroup}>
              {(["all", "synced", "unsynced"] as SyncFilter[]).map((v) => (
                <button
                  key={v}
                  className={`${styles.toggleBtn} ${filters.sync === v ? styles.active : ""}`}
                  onClick={() => setFilter("sync", v)}
                >
                  {v === "all" ? "Todos" : v === "synced" ? "Sim" : "Não"}
                </button>
              ))}
            </div>
          </div>

          <div className={styles.filterGroup}>
            <span className={styles.filterLabel}>Impressão</span>
            <div className={styles.toggleGroup}>
              {(["all", "success", "pending", "error"] as PrintFilter[]).map((v) => (
                <button
                  key={v}
                  className={`${styles.toggleBtn} ${filters.print === v ? styles.active : ""}`}
                  onClick={() => setFilter("print", v)}
                >
                  {v === "all" ? "Todos" : v === "success" ? "OK" : v === "pending" ? "Pendente" : "Erro"}
                </button>
              ))}
            </div>
          </div>

          {hasActiveFilters && (
            <button className={styles.clearBtn} onClick={onClearFilters}>
              Limpar
            </button>
          )}
        </div>
      </div>

      <div className={styles.resultsInfo}>
        {filtered.length} pedido{filtered.length !== 1 ? "s" : ""}
        {hasActiveFilters ? " encontrado" + (filtered.length !== 1 ? "s" : "") : ""}
      </div>

      <div className={styles.header}>
        <div>ID</div>
        <div>Horário</div>
        <div>Itens</div>
        <div>Valor</div>
        <div>Cartão</div>
        <div>Status</div>
        <div></div>
      </div>

      <div
        className={styles.logContainer}
        style={{
          maxHeight: resolution === ResolutionType.FULL_HD ? 1400 : 800,
        }}
      >
        {kiosk &&
          pageItems.map(
            (orderLogItem) =>
              orderLogItem && (
                <OrderLogItemV3
                  key={orderLogItem.id}
                  onClickPreview={onClickPreview}
                  orderLog={orderLogItem}
                />
              )
          )}

        {filtered.length === 0 && (
          <div className={styles.emptyState}>
            Nenhum pedido encontrado para os filtros aplicados.
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className={styles.pagination}>
          <button
            className={styles.pageButton}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            ‹
          </button>
          <span className={styles.pageInfo}>
            {page + 1} / {totalPages}
          </span>
          <button
            className={styles.pageButton}
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={page === totalPages - 1}
          >
            ›
          </button>
        </div>
      )}

      <BottomModal onClickClose={onCloseModal} showModal={modalPreviewOpen}>
        <>
          {itemSelectedView && <OrderList orderLog={itemSelectedView} defaultShowLogs />}
        </>
      </BottomModal>
    </div>
  );
};
